import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "./Icon";
import "./PitchView.css";

const POSITION_CONFIG = {
  // دروازه‌بان
  GK: { x: 50, y: 91, label: 'دروازه‌ بان' },

  // مدافعان
  RB: { x: 18, y: 76, label: 'دفاع راست' },
  LB: { x: 82, y: 76, label: 'دفاع چپ' },
  CB: { x: 50, y: 76, label: 'مدافع میانی' },
  RWB: { x: 14, y: 69, label: 'وینگ‌بک راست' },
  LWB: { x: 86, y: 69, label: 'وینگ‌بک چپ' },

  // هافبک‌ها
  CDM: { x: 50, y: 62, label: 'هافبک دفاعی' },
  CM: { x: 50, y: 50, label: 'هافبک مرکزی' },
  CAM: { x: 50, y: 38, label: 'هافبک هجومی' },
  RM: { x: 18, y: 50, label: 'هافبک راست' },
  LM: { x: 82, y: 50, label: 'هافبک چپ' },

  // مهاجمان
  RW: { x: 18, y: 25, label: 'وینگر راست' },
  LW: { x: 82, y: 25, label: 'وینگر چپ' },
  ST: { x: 50, y: 18, label: 'مهاجم نوک' },
  CF: { x: 50, y: 25, label: 'مهاجم دوم' },
  SS: { x: 38, y: 22, label: 'مهاجم مکمل' },
};

const POSITION_ALIASES = {
  GOALKEEPER: "GK",
  GOAL_KEEPER: "GK",

  RIGHT_BACK: "RB",
  LEFT_BACK: "LB",
  CENTER_BACK: "CB",
  CENTRE_BACK: "CB",
  DEFENDER: "CB",

  DEFENSIVE_MIDFIELDER: "CDM",
  CENTRAL_MIDFIELDER: "CM",
  CENTRE_MIDFIELDER: "CM",
  ATTACKING_MIDFIELDER: "CAM",

  RIGHT_MIDFIELDER: "RM",
  LEFT_MIDFIELDER: "LM",

  RIGHT_WINGER: "RW",
  LEFT_WINGER: "LW",

  STRIKER: "ST",
  FORWARD: "FW",
  CENTER_FORWARD: "CF",
  CENTRE_FORWARD: "CF",
};

function normalizePosition(value) {
  if (!value) return "CM";

  const raw =
    typeof value === "object"
      ? value.code || value.name || value.title || value.position
      : value;

  const normalized = String(raw)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  return POSITION_ALIASES[normalized] || normalized;
}

function getPosition(player) {
  return (
    player?.mainPosition ||
    player?.position ||
    player?.positionCode ||
    player?.primaryPosition ||
    player?.role ||
    player?.secondPosition ||
    'CM'
  );
}

function getPositionLabel(player) {
  const position = getPosition(player);

  if (typeof position === "object") {
    return (
      position?.name ||
      position?.title ||
      position?.label ||
      POSITION_CONFIG[normalizePosition(position)]?.label ||
      "بازیکن"
    );
  }

  return (
    POSITION_CONFIG[normalizePosition(position)]?.label ||
    String(position)
  );
}

function getCoordinates(player, index, total) {
  const code = normalizePosition(getPosition(player));
  const config = POSITION_CONFIG[code];

  if (!config) {
    const columns = Math.min(total, 5);
    const row = Math.floor(index / columns);
    const col = index % columns;

    return {
      x: 18 + col * (64 / Math.max(columns - 1, 1)),
      y: 50 + row * 10,
    };
  }

  const samePositionPlayers = total;

  const spread =
    samePositionPlayers > 1
      ? ((index % 3) - 1) * 7
      : 0;

  return {
    x: Math.min(90, Math.max(10, config.x + spread)),
    y: config.y,
  };
}

function getFullName(player) {
  return (
    player?.fullName ||
    player?.name ||
    player?.playerName ||
    [player?.firstName, player?.lastName].filter(Boolean).join(" ") ||
    "بازیکن بدون نام"
  );
}

function getPhoto(player) {
  return (
    player?.photo ||
    player?.avatar ||
    player?.image ||
    player?.profileImage ||
    player?.user?.photo ||
    null
  );
}

function getImageUrl(path) {
  if (!path || typeof path !== "string") return null;

  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("data:") ||
    path.startsWith("blob:")
  ) {
    return path;
  }

  const baseUrl =
    process.env.REACT_APP_API_BASE_URL?.replace(/\/api\/v1\/?$/, "") ||
    "";

  if (!baseUrl) return path;

  return `${baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
}

function getClubName(player) {
  return (
    player?.club?.name ||
    player?.clubName ||
    player?.team?.name ||
    player?.teamName ||
    "باشگاه ثبت نشده"
  );
}

function getShirtNumber(player) {
  return (
    player?.shirtNumber ||
    player?.jerseyNumber ||
    player?.number ||
    "—"
  );
}

function getPlayerId(player) {
  return player?.id || player?._id || player?.playerId;
}

function PlayerAvatar({ player, onClick, style }) {
  const name = getFullName(player);
  const photo = getImageUrl(getPhoto(player));
  const position = getPositionLabel(player);
  const number = getShirtNumber(player);

  return (
    <button
      type="button"
      className="pitch-player"
      style={style}
      onClick={(event) => {
        event.stopPropagation();
        onClick(player);
      }}
      aria-label={`مشاهده اطلاعات ${name}`}
    >
      <span className="pitch-player__pulse" />

      <span className="pitch-player__avatar">
        {photo ? (
          <img
            src={photo}
            alt={name}
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <span className="pitch-player__initial">
            {name.charAt(0)}
          </span>
        )}
      </span>

      <span className="pitch-player__number">{number}</span>

      <span className="pitch-player__name">
        {name}
      </span>

      <span className="pitch-player__position">
        {position}
      </span>
    </button>
  );
}

function PlayerPopup({ player, onClose }) {
  if (!player) return null;

  const name = getFullName(player);
  const photo = getImageUrl(getPhoto(player));
  const position = getPositionLabel(player);
  const club = getClubName(player);
  const number = getShirtNumber(player);
  const playerId = getPlayerId(player);

  const age =
    player?.age ||
    player?.playerAge ||
    player?.personalInfo?.age ||
    null;

  const nationality =
    player?.nationality ||
    player?.country ||
    player?.personalInfo?.nationality ||
    null;

  return (
    <div className="pitch-popup">
      <button
        type="button"
        className="pitch-popup__backdrop"
        aria-label="بستن اطلاعات بازیکن"
        onClick={onClose}
      />

      <div className="pitch-popup__card">
        <button
          type="button"
          onClick={onClose}
          className="pitch-popup__close"
          aria-label="بستن"
        >
          <Icon name="close" className="h-5 w-5" />
        </button>

        <div className="pitch-popup__hero">
          <div className="pitch-popup__image">
            {photo ? (
              <img src={photo} alt={name} />
            ) : (
              <span>{name.charAt(0)}</span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <span className="pitch-popup__badge">
              {position}
            </span>

            <h3>{name}</h3>

            <p>{club}</p>
          </div>

          <div className="pitch-popup__jersey">
            <span>شماره</span>
            <strong>{number}</strong>
          </div>
        </div>

        <div className="pitch-popup__stats">
          <div>
            <span>پست اصلی</span>
            <strong>{position}</strong>
          </div>

          <div>
            <span>باشگاه</span>
            <strong>{club}</strong>
          </div>

          {age && (
            <div>
              <span>سن</span>
              <strong>{age}</strong>
            </div>
          )}

          {nationality && (
            <div>
              <span>ملیت</span>
              <strong>{nationality}</strong>
            </div>
          )}
        </div>

        {playerId && (
          <Link
            to={`/players/${playerId}`}
            className="pitch-popup__link"
          >
            مشاهده پروفایل کامل
            <Icon name="arrowLeft" className="h-4 w-4" />
          </Link>
        )}
      </div>
    </div>
  );
}

export default function PitchView({
  players = [],
  title = "چیدمان بازیکنان",
  subtitle = "بازیکنان را انتخاب کنید تا اطلاعات آن‌ ها نمایش داده شود.",
  className = "",
}) {
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const positionedPlayers = useMemo(() => {
    const grouped = {};

    players.forEach((player) => {
      const code = normalizePosition(getPosition(player));

      if (!grouped[code]) grouped[code] = [];
      grouped[code].push(player);
    });

    const result = [];

    Object.entries(grouped).forEach(([code, group]) => {
      group.forEach((player, index) => {
        const config = POSITION_CONFIG[code] || POSITION_CONFIG.CM;

        let offset = 0;

        if (group.length === 2) {
          offset = index === 0 ? -9 : 9;
        }

        if (group.length === 3) {
          offset = [-12, 0, 12][index];
        }

        if (group.length >= 4) {
          offset =
            -14 +
            index *
              (28 / Math.max(group.length - 1, 1));
        }

        result.push({
          player,
          x: Math.min(91, Math.max(9, config.x + offset)),
          y: config.y,
        });
      });
    });

    return result;
  }, [players]);

  return (
    <>
      <section className={`pitch-view ${className}`}>
        <div className="pitch-view__top">
          <div>
            <div className="pitch-view__eyebrow">
              <span />
              چیدمان تاکتیکی
            </div>

            <h3>{title}</h3>

            {subtitle && (
              <p>{subtitle}</p>
            )}
          </div>

          <div className="pitch-view__count">
            <Icon name="users" className="h-4 w-4" />
            {players.length.toLocaleString("fa-IR")} بازیکن
          </div>
        </div>

        {players.length === 0 ? (
          <div className="pitch-view__empty">
            <div>
              <Icon name="users" className="h-9 w-9" />
            </div>

            <h4>بازیکنی برای نمایش وجود ندارد</h4>

            <p>
              اطلاعات بازیکنان این بخش هنوز ثبت نشده است.
            </p>
          </div>
        ) : (
          <div className="pitch">
            <div className="pitch__grass" />

            <div className="pitch__lines">
              <div className="pitch__halfway" />
              <div className="pitch__center-circle" />
              <div className="pitch__center-dot" />

              <div className="pitch__penalty pitch__penalty--top" />
              <div className="pitch__penalty pitch__penalty--bottom" />

              <div className="pitch__goal-area pitch__goal-area--top" />
              <div className="pitch__goal-area pitch__goal-area--bottom" />

              <div className="pitch__goal pitch__goal--top" />
              <div className="pitch__goal pitch__goal--bottom" />
            </div>

            <div className="pitch__shine pitch__shine--one" />
            <div className="pitch__shine pitch__shine--two" />

            <div className="pitch__players">
              {positionedPlayers.map(
                ({ player, x, y }, index) => (
                  <PlayerAvatar
                    key={
                      getPlayerId(player) ||
                      `${getFullName(player)}-${index}`
                    }
                    player={player}
                    onClick={setSelectedPlayer}
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                    }}
                  />
                )
              )}
            </div>

            <div className="pitch__hint">
              <Icon name="info" className="h-4 w-4" />
              برای مشاهده مشخصات، روی بازیکن کلیک کنید
            </div>
          </div>
        )}
      </section>

      <PlayerPopup
        player={selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
      />
    </>
  );
}